/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { IActionContext, IImageData, IImageSettings } from '@msdyn365-commerce/core';
import { MediaLocation, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { getMediaLocationsForSelectedVariant, MediaLocationsForSelectedVariantInput } from '@msdyn365-commerce-modules/retail-actions';

export async function getValidProductImages(
    productId: number,
    channelId: number,
    actionContext: IActionContext,
    imageSettings: IImageSettings,
    selectedProduct?: SimpleProduct
): Promise<IImageData[]> {
    const actionInput = new MediaLocationsForSelectedVariantInput(productId, channelId, selectedProduct);

    return getMediaLocationsForSelectedVariant(actionInput, actionContext)
        .then(mediaLocations => {
            if (mediaLocations) {
                return Promise.all(mediaLocations.map(async mediaLocation => validateMediaLocaionAsync(mediaLocation, imageSettings))).then(
                    pairs => {
                        return pairs.filter(pair => pair[1]).map(pair => pair[0]);
                    }
                );
            }
            return [];
        })
        .catch(error => {
            actionContext.telemetry.exception(error);
            actionContext.telemetry.debug('Unable to get Media Locations for Selected Variant');
            return [];
        });
}

async function validateMediaLocaionAsync(mediaLocation: MediaLocation, imageSettings: IImageSettings): Promise<[IImageData, boolean]> {
    const imageData = {
        src: mediaLocation.Uri || '',
        altText: mediaLocation.AltText || ''
    };

    const imageSettingsVal = imageSettings.viewports.lg!.q!.replace(/["']+/g, '');

    if (imageData.src === '') {
        return [imageData, false];
    }

    const imageConcatVal = `${imageData.src}` + '&' + `${imageSettingsVal}`;

    return new Promise<[IImageData, boolean]>(resolve => {
        try {
            const http = new XMLHttpRequest();
            http.open('HEAD', imageConcatVal, true);

            http.addEventListener('load', () => {
                resolve([imageData, http.status === 200 || http.status === 201]);
            });

            http.addEventListener('error', () => {
                resolve([imageData, false]);
            });

            http.send();
        } catch {
            resolve([imageData, false]);
        }
    });
}
